import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  TICKET_SOURCES,
  TICKET_STATES,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { PageRoot, PageHeader, Toolbar } from '../ui/Page';
import { SearchInput } from '../ui/SearchInput';
import { DataTable } from '@/components/ui/data-table';
import { useServerTable } from '../ui/useServerTable';
import { UnderlineTabStrip, UnderlineTabs } from '../ui/Tabs';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState, isNotFound } from '../ui/QueryStates';
import { Skeleton } from '../ui/Skeleton';
import { Button } from '../ui/Button';
import { WithOrgClock } from '../ui/WithOrgClock';
import { IconCheck, IconPlus, IconTickets } from '../icons';
import { NewTicketSheet } from '../create/NewTicketSheet';
import { TicketBoard } from '../tickets/TicketBoard';
import { TicketPanel } from '../tickets/TicketPanel';
import { ticketColumns, ticketName, ticketRowLabel } from '../tickets/TicketBits';
import { useNow } from '../ui/useNow';
import { field } from '../create/Field';
import { cn } from '@/lib/utils';
import {
  boardInputFor,
  NO_FILTERS,
  categoryLabel,
  filtering,
  filtersOf,
  listInputFor,
  mayOwnTickets,
  priorityLabel,
  sourceLabel,
  stateLabel,
  linkedOffView,
  ticketsSearch,
  type TicketFilters,
  type TicketsSearch,
  type TicketView,
} from '../tickets/ticketView';
import type { Member } from '../tickets/types';

const VIEW_TABS = [
  { key: 'list', label: 'List' },
  { key: 'board', label: 'Board' },
];

/**
 * The Tickets page (ops insights Phase 5a, ticket 04; spec §5): every problem after a visit,
 * as a List (a server-paged table) or a Board (a column per status), for the owner and the
 * office. The view, the filters and the open ticket all live in the address
 * (`/tickets?tab=board&owner=mine&ticket=<id>`, `ticketsSearch`), so a link from the bell, the
 * job page or MCP lands on the same view with the panel open and the item marked.
 */
export function TicketsPage() {
  return (
    <WithOrgClock what="tickets" icon={IconTickets} title="Tickets">
      {(tz) => <Tickets tz={tz} />}
    </WithOrgClock>
  );
}

/** An address with the keys it does not set left out, so links have one spelling. */
function cleaned(s: TicketsSearch): TicketsSearch {
  return Object.fromEntries(
    Object.entries(s).filter(([, v]) => v !== undefined),
  ) as TicketsSearch;
}

function Tickets({ tz }: { tz: string }) {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const raw: Record<string, unknown> = useSearch({ strict: false });
  const search = ticketsSearch(raw);
  const filters = filtersOf(search);
  const view: TicketView = search.tab ?? 'list';
  const now = useNow();
  const [creating, setCreating] = useState(false);

  /** Change the address: a functional update, so what it does not name survives. */
  const setSearch = (patch: Partial<TicketsSearch>) => {
    const merge = (prev: Record<string, unknown>) =>
      cleaned({ ...ticketsSearch(prev), ...patch });
    void navigate({
      to: '.',
      // Route-generic (`to: '.'`); the route's own `validateSearch` checks it again.
      search: merge as never,
      replace: true,
      resetScroll: false,
    });
  };
  const setFilter = (patch: Partial<TicketFilters>) => setSearch(patch);
  const clearFilters = () => setSearch(NO_FILTERS);

  // The address's filters narrow the result set from outside the table: they join its reset.
  const {
    search: text,
    setSearch: setText,
    query,
    table,
  } = useServerTable(undefined, JSON.stringify(filters));
  const list = useQuery({
    ...trpc.ticket.list.queryOptions(listInputFor(filters, query)),
    enabled: view === 'list',
    placeholderData: (prev) => prev,
  });
  const board = useQuery({
    ...trpc.ticket.board.queryOptions(boardInputFor(filters, query.search)),
    enabled: view === 'board',
    placeholderData: (prev) => prev,
  });
  const members = useQuery(trpc.org.members.queryOptions());
  const roster: readonly Member[] = members.data ?? [];

  // A link lands on the exact item (UX baseline). When the open ticket is drawn in this view,
  // its row or card is marked (`aria-current`) by the view itself; when it is not (filtered
  // out, on another page, resolved more than 14 days ago), it is pinned above the view as a
  // marked anchor that says so. Either is scrolled into view ONCE per opened id, so a refetch
  // never pulls the page back to it while someone is reading elsewhere.
  //
  // The anchor outlives the panel (`pinned`): the panel is modal, so its "Clear search and
  // filters" is reachable only once the panel is closed. It goes when it is dismissed, or for
  // good once the ticket is in the view again (a later filter that hides it again does not
  // bring an old anchor back). A linked ticket that no longer exists gets a notice instead,
  // with nothing to open or clear.
  const openId = search.ticket;
  const [pinned, setPinned] = useState<string | undefined>(openId);
  const [pinnedFor, setPinnedFor] = useState(openId);
  if (openId !== pinnedFor) {
    setPinnedFor(openId);
    if (openId !== undefined) setPinned(openId);
  }
  const anchorId = openId ?? pinned;
  const shownIds: readonly string[] | null =
    view === 'list'
      ? (list.data?.rows.map((r) => r.id) ?? null)
      : (board.data?.columns.flatMap((c) => c.rows.map((r) => r.id)) ?? null);
  if (pinned !== undefined && shownIds?.includes(pinned)) setPinned(undefined);
  const offView = linkedOffView(anchorId, shownIds);
  const linked = useQuery({
    ...trpc.ticket.get.queryOptions({ ticketId: anchorId ?? '' }),
    enabled: anchorId !== undefined,
  });
  const linkedGone = linked.isError && isNotFound(linked.error);
  const scrolledFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!openId) {
      scrolledFor.current = undefined;
      return;
    }
    if (scrolledFor.current === openId || typeof document.querySelector !== 'function')
      return;
    const el = document.querySelector(
      offView
        ? '[data-anchor="linked-ticket"]'
        : `[data-row-key="${openId}"], [data-ticket-key="${openId}"]`,
    );
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      scrolledFor.current = openId;
    }
  }, [openId, offView, view, list.data, board.data]);

  const open = (ticketId: string) => setSearch({ ticket: ticketId });
  // The Board shows no status filter (a column IS a status), so one left in the address does
  // not count there: "Clear filters" is offered only for what the view shows.
  const anyFilter = filtering(
    view === 'board' ? { ...filters, status: undefined } : filters,
  );
  const owners = roster.filter((m) => mayOwnTickets(m.role));

  return (
    <PageRoot>
      <PageHeader
        title="Tickets"
        sub="Problems after a visit, each owned until it is resolved."
        right={
          <Button onClick={() => setCreating(true)}>
            <IconPlus size={15} />
            New ticket
          </Button>
        }
      />
      <UnderlineTabs
        value={view}
        onValueChange={(v) => setSearch({ tab: v === 'board' ? 'board' : undefined })}
      >
        <UnderlineTabStrip tabs={VIEW_TABS} />
      </UnderlineTabs>

      <Toolbar>
        <SearchInput
          placeholder="Search tickets…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label="Search tickets"
        />
        {view === 'list' ? (
          <FilterSelect
            label="Status"
            value={filters.status ?? ''}
            any="Any status"
            options={TICKET_STATES.map((s) => ({ value: s, label: stateLabel(s) }))}
            onChange={(v) =>
              setFilter({ status: (v || undefined) as TicketFilters['status'] })
            }
          />
        ) : null}
        <FilterSelect
          label="Owner"
          value={filters.owner ?? ''}
          any="Anyone"
          options={[
            { value: 'mine', label: 'Mine' },
            { value: 'nobody', label: 'Nobody yet' },
            ...owners.map((m) => ({ value: m.userId, label: m.name ?? m.email })),
            // A link naming someone no longer on the roster still says who it filters by.
            ...(filters.owner &&
            filters.owner !== 'mine' &&
            filters.owner !== 'nobody' &&
            !owners.some((m) => m.userId === filters.owner)
              ? [{ value: filters.owner, label: 'Someone else' }]
              : []),
          ]}
          onChange={(v) => setFilter({ owner: v || undefined })}
        />
        <FilterSelect
          label="Priority"
          value={filters.priority ?? ''}
          any="Any priority"
          options={TICKET_PRIORITIES.map((p) => ({ value: p, label: priorityLabel(p) }))}
          onChange={(v) =>
            setFilter({ priority: (v || undefined) as TicketFilters['priority'] })
          }
        />
        <FilterSelect
          label="Category"
          value={filters.category ?? ''}
          any="Any category"
          options={TICKET_CATEGORIES.map((c) => ({ value: c, label: categoryLabel(c) }))}
          onChange={(v) =>
            setFilter({ category: (v || undefined) as TicketFilters['category'] })
          }
        />
        <FilterSelect
          label="Source"
          value={filters.source ?? ''}
          any="Any source"
          // Named in each option, so a chosen one still says what it filters ("Source:
          // Customer"), beside a list that already has a Customer chip and column words.
          options={TICKET_SOURCES.map((v) => ({
            value: v,
            label: `Source: ${sourceLabel(v)}`,
          }))}
          onChange={(v) =>
            setFilter({ source: (v || undefined) as TicketFilters['source'] })
          }
        />
        <Button
          variant="ghost"
          aria-pressed={filters.overdue === true}
          className="aria-pressed:border-brand aria-pressed:bg-[var(--sw-brand-pill)] aria-pressed:text-[var(--sw-brand-text)]"
          onClick={() => setFilter({ overdue: filters.overdue ? undefined : true })}
        >
          {filters.overdue ? <IconCheck size={14} /> : null}
          Overdue only
        </Button>
        {anyFilter ? (
          <Button variant="ghost" onClick={clearFilters}>
            Clear filters
          </Button>
        ) : null}
      </Toolbar>

      {offView && anchorId && linkedGone ? (
        <GoneLinkNotice
          onDismiss={() => {
            setPinned(undefined);
            if (openId !== undefined) setSearch({ ticket: undefined });
          }}
        />
      ) : offView && anchorId ? (
        <LinkedTicketAnchor
          name={linked.data ? ticketName(linked.data.ticket) : 'This ticket'}
          onOpen={() => open(anchorId)}
          // Clearing lands on the ticket: the filters go and its panel opens over its row.
          onClear={
            anyFilter || text.trim() !== ''
              ? () => {
                  setText('');
                  setSearch({ ...NO_FILTERS, ticket: anchorId });
                }
              : undefined
          }
          onDismiss={openId === undefined ? () => setPinned(undefined) : undefined}
        />
      ) : null}

      {view === 'list' ? (
        list.isPending ? (
          <Skeleton className="h-64 w-full" />
        ) : list.isError ? (
          <ErrorState
            icon={IconTickets}
            what="tickets"
            onRetry={() => void list.refetch()}
          />
        ) : (
          <DataTable
            columns={ticketColumns(tz, now)}
            rows={list.data.rows}
            rowKey={(t) => t.id}
            search={text}
            server={table(list.data.total, list.isFetching)}
            onRowClick={(t) => open(t.id)}
            rowLabel={ticketRowLabel}
            rowOpens="dialog"
            activeKey={openId}
            empty={
              <TicketsEmpty
                filtered={anyFilter || text.trim() !== ''}
                onClear={clearFilters}
              />
            }
          />
        )
      ) : board.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : board.isError ? (
        <ErrorState
          icon={IconTickets}
          what="the board"
          onRetry={() => void board.refetch()}
        />
      ) : (
        <TicketBoard
          columns={board.data.columns}
          filters={filters}
          openId={openId}
          onOpen={open}
          tz={tz}
          now={now}
        />
      )}

      <TicketPanel
        ticketId={openId ?? null}
        tz={tz}
        now={now}
        members={roster}
        onClose={() => setSearch({ ticket: undefined })}
      />
      <NewTicketSheet
        open={creating}
        onClose={() => setCreating(false)}
        tz={tz}
        onCreated={open}
      />
    </PageRoot>
  );
}

/**
 * The open ticket is not drawn in this view: say so, above it, marked as the linked item, with
 * a way to it (the panel) and, when filters or a search hide it, the way to clear them.
 */
function LinkedTicketAnchor({
  name,
  onOpen,
  onClear,
  onDismiss,
}: {
  name: string;
  onOpen: () => void;
  onClear: (() => void) | undefined;
  /** Offered once the panel is closed; while it is open the anchor is its ticket's place. */
  onDismiss: (() => void) | undefined;
}) {
  return (
    <div
      data-anchor="linked-ticket"
      aria-current="true"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[var(--radius)] border border-brand bg-[var(--sw-brand-pill)] p-3 text-sm"
    >
      <span className="font-medium">Opened ticket:</span>
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={onOpen}
        className="min-w-0 break-words font-medium text-[var(--sw-brand-text)] underline underline-offset-2"
      >
        {name}
      </button>
      <span className="text-muted-foreground">isn&apos;t in the current view.</span>
      {onClear ? (
        // A search hides a ticket as surely as a filter does, so both go.
        <Button variant="ghost" size="sm" onClick={onClear}>
          Clear search and filters
        </Button>
      ) : null}
      {onDismiss ? (
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      ) : null}
    </div>
  );
}

/** The linked ticket is gone (deleted, or an old link): say so, and offer only to dismiss it. */
function GoneLinkNotice({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      data-anchor="linked-ticket"
      data-gone="true"
      role="status"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[var(--radius)] border border-border bg-surface p-3 text-sm"
    >
      <span>That ticket no longer exists.</span>
      <Button variant="ghost" size="sm" onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  );
}

/** One filter: a labelled select whose first option is "any". */
function FilterSelect({
  label,
  value,
  any,
  options,
  onChange,
}: {
  label: string;
  value: string;
  any: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <select
      aria-label={label}
      data-filter={label.toLowerCase()}
      className={cn(field, 'w-auto')}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{any}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** No hooks. Honest about the filters: "No tickets yet" under a filter would be a lie. */
export function TicketsEmpty({
  filtered,
  onClear,
}: {
  filtered: boolean;
  onClear: () => void;
}) {
  return filtered ? (
    <EmptyState
      icon={IconTickets}
      title="No tickets match"
      description="Nothing matches this search and these filters."
      action={
        <Button variant="ghost" onClick={onClear}>
          Clear filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={IconTickets}
      title="No tickets yet"
      description="When something goes wrong after a visit, raise a ticket so someone owns it until it is resolved."
    />
  );
}
